import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import { format, startOfDay, endOfDay, subDays } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import EntityConstants from "../../configs/EntityConstants";
import { GET } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import IssuedCredentialLongCard from "../../components/Cards/IssuedCredentialLongCard/IssuedCredentialLongCard";
import Dropdown from "../../common/Dropdown/Dropdown";
import AuthContext from "../../store/auth-context";
import { CalendarDays } from "lucide-react";
import { fetchOrganisationsAPI } from "../../common/Organisations/fetchOrganisations";
import { fetchCurrentUser } from "../../service/CurrentUser";

const ISSUANCE_STATUS_POLLING_INTERVAL = 2000;
const SEARCH_DEBOUNCE_DELAY = 500;

const AllTransactions = () => {
  const authContext = useContext(AuthContext);
  const [limit] = useState(6);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(search);
  const [previous, setPrevious] = useState(null);
  const [next, setNext] = useState(null);
  const [respContentList, setRespContentList] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [programmes, setProgrammes] = useState([]);
  const [selectedProgramme, setSelectedProgramme] = useState("");
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const pollingIntervals = useRef({});
  const [selectedOrganisation, setSelectedOrganisation] = useState("");
  const [org_code, setOrgCode] = useState("");
  const [userType, setUserType] = useState(null);
  const [tenantCode, setTenantCode] = useState("");

  const [organisationOptions, setOrganisationOptions] = useState([
    { name: "All Organisations", value: "" },
  ]);
  const [orgNext, setOrgNext] = useState(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [orgOffset, setOrgOffset] = useState(0);
  const [hasOrganisations, setHasOrganisations] = useState(false);
  const orgDropdownRef = useRef(null);
  const ORG_PAGE_SIZE = 10;
  const normalizedUserType = (userType ?? "").toLowerCase();

  useEffect(() => {
    const loadData = async () => {
      const data = await fetchCurrentUser();
      if (data) {
        setUserType(data.type);
        setTenantCode(data.tenant_code);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (
      tenantCode &&
      (normalizedUserType === "tenant_admin" ||
        normalizedUserType === "tenant_user")
    ) {
      fetchOrgs();
    }
  }, [tenantCode, normalizedUserType]);

  const fetchOrgs = async (loadMore = false) => {
    try {
      setOrgLoading(true);
      const offset = loadMore ? orgOffset : 0;
      const data = await fetchOrganisationsAPI(
        tenantCode,
        offset,
        ORG_PAGE_SIZE
      );

      if (!loadMore && data.options.length <= 1) {
        setHasOrganisations(false);
        setOrganisationOptions([{ name: "All Organisations", value: "" }]);
      } else {
        setHasOrganisations(true);
        if (loadMore) {
          const newOptions = data.options.filter((opt) => opt.value !== "");
          setOrganisationOptions((prev) => [...prev, ...newOptions]);
        } else {
          setOrganisationOptions(data.options);
        }
      }

      setOrgNext(data.next);
      setOrgOffset(offset + ORG_PAGE_SIZE);
    } catch (error) {
      console.error("Error fetching organisations:", error);
      setHasOrganisations(false);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to fetch organisations",
      });
    } finally {
      setOrgLoading(false);
    }
  };

  const handleOrgScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 1 &&
      orgNext &&
      !orgLoading
    ) {
      fetchOrgs(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        orgDropdownRef.current &&
        !orgDropdownRef.current.contains(event.target)
      ) {
        setOrgDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!orgDropdownOpen) {
      setOrgOffset(0);
    }
  }, [orgDropdownOpen]);

  useEffect(() => {
    if (authContext.issuedOffset !== undefined) {
      authContext.IssuedOffsetHandler(0);
      if (debouncedSearchTerm) {
        SEARCH(
          limit,
          0,
          status,
          debouncedSearchTerm,
          selectedProgramme,
          selectedBatch
        );
      } else {
        GET_ALL_TRANSACTIONS_LIST(
          limit,
          0,
          status,
          selectedProgramme,
          selectedBatch
        );
      }
    }
  }, [org_code]);

  const formatDateForAPI = (date) => {
    if (!date) return "";
    return new Date(date).toISOString();
  };

  const getDateRangeForAPI = () => {
    const now = new Date();
    let startDate, endDate;

    switch (dateRange) {
      case "recent":
        startDate = subDays(startOfDay(now), 6);
        endDate = endOfDay(now);
        break;
      case "last30days":
        startDate = subDays(startOfDay(now), 29);
        endDate = endOfDay(now);
        break;
      case "last3months":
        startDate = subDays(startOfDay(now), 89);
        endDate = endOfDay(now);
        break;
      case "last6months":
        startDate = subDays(startOfDay(now), 179);
        endDate = endOfDay(now);
        break;
      case "all":
        return { all_transactions: true };
      case "custom":
        if (customStartDate && customEndDate) {
          startDate = startOfDay(customStartDate);
          endDate = endOfDay(customEndDate);
        } else {
          startDate = subDays(startOfDay(now), 6);
          endDate = endOfDay(now);
        }
        break;
      default:
        startDate = subDays(startOfDay(now), 6);
        endDate = endOfDay(now);
    }

    return {
      from: formatDateForAPI(startDate),
      to: formatDateForAPI(endDate),
    };
  };

  const updateStatus = (value) => {
    if (status !== value) {
      authContext.IssuedOffsetHandler(0);
    }
    setStatus(value);
  };

  const options2 = [
    { name: "All", value: "" },
    { name: "Issued", value: "issued" },
    { name: "In Progress", value: "in-progress" },
    { name: "Revoked", value: "revoked" },
    { name: "Failed", value: "failed" },
    { name: "Partially Issued", value: "partially-issued" },
  ];

  const programmeOptions = [
    { name: "All Programmes", value: "" },
    ...programmes.map((programme) => ({
      name: programme.programme_name,
      value: programme.programme_code,
    })),
  ];

  const batchOptions = [
    { name: "All Batches", value: "" },
    ...batches.map((batch) => ({
      name: batch.batch_name,
      value: batch.batch_code,
    })),
  ];

  const fetchIssuanceStatus = async (transaction_id) => {
    try {
      const response = await GET(
        `${EntityConstants.GET_ISSUANCE_STATUS}?transaction_id=${transaction_id}`
      );
      return await response.json();
    } catch (error) {
      console.error("Error fetching issuance status:", error);
      return null;
    }
  };

  const setupPollingForInProgressTransactions = (transactions) => {
    transactions.forEach(async (txn) => {
      if (
        (txn.transaction_status === "Partially Issued" ||
          txn.transaction_status === "Partially Revoked" ||
          txn.transaction_status === "Partially Issued and Revoked") &&
        !txn.issuance_status
      ) {
        try {
          const statusData = await fetchIssuanceStatus(txn.transaction_id);
          if (statusData) {
            setRespContentList((prevTxns) =>
              prevTxns.map((t) =>
                t.transaction_id === txn.transaction_id
                  ? {
                      ...t,
                      issuance_status: statusData,
                      progress: `${statusData.issued}/${statusData.total_recipients}`,
                    }
                  : t
              )
            );
          }
        } catch (error) {
          console.error("Error fetching issuance status:", error);
        }
      }

      if (
        txn.transaction_status === "In Progress" &&
        !pollingIntervals.current[txn.transaction_id]
      ) {
        pollingIntervals.current[txn.transaction_id] = setInterval(async () => {
          try {
            const statusData = await fetchIssuanceStatus(txn.transaction_id);
            if (statusData) {
              setRespContentList((prevTxns) =>
                prevTxns.map((t) =>
                  t.transaction_id === txn.transaction_id
                    ? {
                        ...t,
                        issuance_status: statusData,
                        progress: `${statusData.issued}/${statusData.total_recipients}`,
                      }
                    : t
                )
              );

              const pending =
                statusData.total_recipients -
                statusData.issued -
                statusData.failed;

              if (
                pending <= 0 ||
                statusData.transaction_status !== "In Progress"
              ) {
                clearInterval(pollingIntervals.current[txn.transaction_id]);
                delete pollingIntervals.current[txn.transaction_id];

                if (debouncedSearchTerm) {
                  SEARCH(
                    limit,
                    authContext.issuedOffset,
                    status,
                    debouncedSearchTerm,
                    selectedProgramme,
                    selectedBatch,
                    true,
                    selectedOrganisation
                  );
                } else {
                  GET_ALL_TRANSACTIONS_LIST(
                    limit,
                    authContext.issuedOffset,
                    status,
                    selectedProgramme,
                    selectedBatch,
                    true,
                    selectedOrganisation
                  );
                }
              }
            }
          } catch (error) {
            console.error("Polling error:", error);
          }
        }, ISSUANCE_STATUS_POLLING_INTERVAL);
      }
    });

    Object.keys(pollingIntervals.current).forEach((id) => {
      const txn = transactions.find(
        (t) => t.transaction_id === id && t.transaction_status === "In Progress"
      );
      if (!txn) {
        clearInterval(pollingIntervals.current[id]);
        delete pollingIntervals.current[id];
      }
    });
  };

  const GET_ALL_TRANSACTIONS_LIST = async (
    limit,
    offset,
    status,
    programme_code = "",
    batch_code = "",
    silent = false
  ) => {
    if (!silent) setIsSearching(true);
    const temp_array = respContentList;
    if (!silent) setRespContentList([]);

    const dateRangeParams = getDateRangeForAPI();

    let url = `${EntityConstants.GET_TRANSACTIONS_DETAILS}?limit=${limit}&offset=${offset}&status=${status}`;

    if (dateRangeParams.all_transactions) {
      url += `&all_transactions=true`;
    } else {
      url += `&from=${encodeURIComponent(
        dateRangeParams.from
      )}&to=${encodeURIComponent(dateRangeParams.to)}`;
    }

    if (programme_code) {
      url += `&programme_code=${encodeURIComponent(programme_code)}`;
    }
    if (batch_code) {
      url += `&batch_code=${encodeURIComponent(batch_code)}`;
    }
    if (org_code) {
      url += `&org_code=${org_code}`;
    }

    try {
      const response = await GET(url);
      const data = await response.json();

      if (data.error) {
        Toast({
          type: "error",
          title: "Error",
          message: data.error,
        });
        setRespContentList(temp_array);
      } else {
        authContext.IssuedOffsetHandler(offset);
        setRespContentList(data.results);
        setPrevious(data.previous);
        setNext(data.next);
        setupPollingForInProgressTransactions(data.results);
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (!silent) setIsSearching(false);
      setIsSearched(true);
    }
  };

  const SEARCH = (
    limit,
    offset,
    status,
    search,
    programme_code = "",
    batch_code = "",
    silent = false
  ) => {
    if (!silent) setIsSearching(true);

    const dateRangeParams = getDateRangeForAPI();

    let url = `${EntityConstants.GET_TRANSACTIONS_DETAILS}?limit=${limit}&offset=${offset}&status=${status}&search=${search}`;

    if (dateRangeParams.all_transactions) {
      url += `&all_transactions=true`;
    } else {
      url += `&from=${encodeURIComponent(
        dateRangeParams.from
      )}&to=${encodeURIComponent(dateRangeParams.to)}`;
    }

    if (programme_code) {
      url += `&programme_code=${encodeURIComponent(programme_code)}`;
    }
    if (batch_code) {
      url += `&batch_code=${encodeURIComponent(batch_code)}`;
    }
    if (org_code) {
      url += `&org_code=${org_code}`;
    }

    GET(url)
      .then((response) => response.json())
      .then((data) => {
        setRespContentList(data.results);
        setPrevious(data.previous);
        setNext(data.next);
        setupPollingForInProgressTransactions(data.results);
        setIsSearched(true);
        setIsSearching(false);
      })
      .catch((error) => {
        console.error("Error fetching search results:", error);
        setIsSearching(false);
        setIsSearched(false);
      });
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(search);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  useEffect(() => {
    if (debouncedSearchTerm) {
      SEARCH(
        limit,
        authContext.issuedOffset,
        status,
        debouncedSearchTerm,
        selectedProgramme,
        selectedBatch
      );
    } else {
      GET_ALL_TRANSACTIONS_LIST(
        limit,
        authContext.issuedOffset,
        status,
        selectedProgramme,
        selectedBatch
      );
    }
  }, [
    debouncedSearchTerm,
    status,
    limit,
    authContext.issuedOffset,
    selectedProgramme,
    selectedBatch,
    dateRange,
    customStartDate,
    customEndDate,
  ]);

  useEffect(() => {
    const fetchProgrammes = () => {
      GET(`${EntityConstants.GET_PROGRAMME_DETAILS}?limit=100&offset=0`)
        .then((response) => response.json())
        .then((data) => {
          if (data.error) {
            Toast({
              type: "error",
              title: "Error",
              message: data.error,
            });
          } else {
            const activeProgrammes = data.data.results.filter(
              (programme) => programme.is_active
            );
            setProgrammes(activeProgrammes);
          }
        })
        .catch((error) => {
          console.error("Error fetching programmes:", error);
          Toast({
            type: "error",
            title: "Error",
            message: "Failed to fetch programmes",
          });
        });
    };
    fetchProgrammes();
  }, []);

  useEffect(() => {
    const fetchBatches = () => {
      if (selectedProgramme === "") {
        setBatches([]);
        setSelectedBatch("");
        return;
      }

      const selectedProgrammeObj = programmes.find(
        (programme) => programme.programme_code === selectedProgramme
      );

      if (!selectedProgrammeObj) {
        setBatches([]);
        setSelectedBatch("");
        return;
      }

      GET(
        `${EntityConstants.GET_BATCH_DETAILS}?programme_code=${selectedProgrammeObj.programme_code}&limit=100&offset=0`
      )
        .then((response) => response.json())
        .then((data) => {
          if (data.error) {
            Toast({
              type: "error",
              title: "Error",
              message: data.error,
            });
          } else {
            const activeBatches = data.data.results.filter(
              (batch) => batch.is_active
            );
            setBatches(activeBatches);
            setSelectedBatch("");
          }
        })
        .catch((error) => {
          console.error("Error fetching batches:", error);
          Toast({
            type: "error",
            title: "Error",
            message: "Failed to fetch batches",
          });
        });
    };
    fetchBatches();
  }, [selectedProgramme, programmes]);

  useEffect(() => {
    if (authContext.issuedOffset !== null) {
      localStorage.setItem("transactionOffset", authContext.issuedOffset);
    }
  }, [authContext.issuedOffset]);

  const DateFilterDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [tempStartDate, setTempStartDate] = useState(customStartDate);
    const [tempEndDate, setTempEndDate] = useState(customEndDate);

    const today = useMemo(() => new Date(), []);

    const minStartDate = useMemo(() => {
      const minDate = new Date(today);
      minDate.setDate(minDate.getDate() - 180);
      return minDate;
    }, [today]);

    const maxEndDate = useMemo(() => {
      if (!tempStartDate) return today;

      const maxFromStartDate = new Date(tempStartDate);
      maxFromStartDate.setDate(maxFromStartDate.getDate() + 180);

      return maxFromStartDate < today ? maxFromStartDate : today;
    }, [tempStartDate, today]);

    const minEndDate = useMemo(() => {
      return tempStartDate || minStartDate;
    }, [tempStartDate, minStartDate]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const presetOptions = [
      { label: "Recent", value: "recent" },
      { label: "Last 30 Days", value: "last30days" },
      { label: "Last 3 Months", value: "last3months" },
      { label: "Last 6 Months", value: "last6months" },
      { label: "All", value: "all" },
    ];

    const handlePresetSelect = (preset) => {
      setDateRange(preset);
      setCustomStartDate(null);
      setCustomEndDate(null);
      setIsOpen(false);
    };

    const handleApplyCustomDate = () => {
      if (tempStartDate && tempEndDate && tempEndDate >= tempStartDate) {
        setCustomStartDate(tempStartDate);
        setCustomEndDate(tempEndDate);
        setDateRange("custom");
        setIsOpen(false);
      }
    };

    const getDisplayText = () => {
      if (dateRange === "custom" && customStartDate && customEndDate) {
        return `${format(customStartDate, "MMM d, yyyy")} - ${format(
          customEndDate,
          "MMM d, yyyy"
        )}`;
      }
      return (
        presetOptions.find((opt) => opt.value === dateRange)?.label || "Recent"
      );
    };

    return (
      <div className="relative inline-block text-left" ref={dropdownRef}>
        <button
          type="button"
          className="inline-flex justify-between items-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
          onClick={() => setIsOpen(!isOpen)}
        >
          {getDisplayText()}
          <svg
            className="-mr-1 ml-2 h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {isOpen && (
          <div className="origin-top-left absolute left-0 mt-2 w-[23rem] rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
            <div className="" role="menu" aria-orientation="vertical">
              {presetOptions.map((option) => (
                <button
                  key={option.value}
                  className={`${
                    dateRange === option.value
                      ? "bg-gray-100 text-gray-900"
                      : "text-gray-700"
                  } block w-full text-left px-4 py-2 text-sm hover:bg-gray-100`}
                  onClick={() => handlePresetSelect(option.value)}
                >
                  {option.label}
                </button>
              ))}

              <hr className=" border-gray-200" />

              <div className="px-4 py-2 text-sm text-gray-500 font-medium">
                Custom period:
              </div>

              <div className="px-4 py-2 space-y-2">
                <div className="flex items-center space-x-3">
                  <div className="relative w-[10rem]">
                    <DatePicker
                      selected={tempStartDate}
                      onChange={(date) => {
                        setTempStartDate(date);

                        if (!date) {
                          setTempEndDate(null);
                          return;
                        }

                        // Clear end date if it's before the new start date
                        if (tempEndDate && tempEndDate < date) {
                          setTempEndDate(null);
                          return;
                        }

                        // Clear end date if it exceeds the new max range
                        const maxFromStart = new Date(date);
                        maxFromStart.setDate(maxFromStart.getDate() + 180);
                        const upperBound =
                          maxFromStart < today ? maxFromStart : today;

                        if (tempEndDate && tempEndDate > upperBound) {
                          setTempEndDate(null);
                        }
                      }}
                      selectsStart
                      startDate={tempStartDate}
                      endDate={tempEndDate}
                      dateFormat={"dd/MM/yyyy"}
                      className="w-full border border-gray-300 rounded pl-12 pr-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      placeholderText="Start date"
                      minDate={minStartDate}
                      maxDate={today}
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      yearDropdownItemNumber={15}
                      scrollableYearDropdown
                    />
                    <CalendarDays className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 pointer-events-none" />
                  </div>

                  <div className="relative w-[10rem]">
                    <DatePicker
                      selected={tempEndDate}
                      onChange={(date) => setTempEndDate(date)}
                      selectsEnd
                      startDate={tempStartDate}
                      endDate={tempEndDate}
                      dateFormat={"dd/MM/yyyy"}
                      minDate={minEndDate}
                      maxDate={maxEndDate}
                      className="w-full border border-gray-300 rounded pl-12 pr-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      placeholderText="End date"
                      disabled={!tempStartDate}
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      yearDropdownItemNumber={15}
                      scrollableYearDropdown
                    />
                    <CalendarDays className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 pointer-events-none" />
                  </div>
                </div>
                {!tempStartDate ? (
                  <div className="text-xs text-gray-500">
                    Select a start date between{" "}
                    {format(minStartDate, "MMM d, yyyy")} and{" "}
                    {format(today, "MMM d, yyyy")}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">
                    End date must be between{" "}
                    {format(tempStartDate, "MMM d, yyyy")} and{" "}
                    {format(today, "MMM d, yyyy")}
                  </div>
                )}

                <button
                  onClick={handleApplyCustomDate}
                  disabled={
                    !tempStartDate ||
                    !tempEndDate ||
                    tempEndDate < tempStartDate
                  }
                  className="w-full bg-[#3538CD] text-white font-medium py-2 px-4 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed mt-3"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const updateProgramme = (value) => {
    setSelectedProgramme(value);
  };

  const updateBatch = (value) => {
    setSelectedBatch(value);
  };

  return (
    <div className="flex flex-col items-center w-full pb-3 pt-20 bg-cfSecondary min-h-screen ">
      <div className="w-full flex flex-col">
        <div
          className={`w-full bg-cfSecondary flex justify-between py-[0.8rem] px-8`}
        >
          <div
            className={`flex flex-col lg-xl:flex-row items-start lg-xl:items-center justify-between w-full`}
          >
            <div className="flex flex-col lg-xl:flex-row items-start lg-xl:items-center gap-2">
              <div className="flex items-center gap-2">
                <DateFilterDropdown />
                <Dropdown
                  options={options2}
                  selected={
                    options2.find((option) => option.value === status) ||
                    options2[0]
                  }
                  onSelect={updateStatus}
                  buttonClass="border"
                  menuClass="z-20"
                />
              </div>
              {/* Group 2: Programme and Batch Filters */}
              <div className="flex items-center gap-2">
                <div className="w-[200px]">
                  <Dropdown
                    options={programmeOptions}
                    selected={
                      programmeOptions.find(
                        (option) => option.value === selectedProgramme
                      ) || programmeOptions[0]
                    }
                    onSelect={updateProgramme}
                    buttonClass="border"
                    menuClass="z-20"
                  />
                </div>
                <div>
                  <Dropdown
                    options={batchOptions}
                    selected={
                      batchOptions.find(
                        (option) => option.value === selectedBatch
                      ) || batchOptions[0]
                    }
                    onSelect={updateBatch}
                    buttonClass="border"
                    menuClass="z-20"
                  />
                </div>
                {(normalizedUserType === "tenant_admin" ||
                  normalizedUserType === "tenant_user") &&
                  hasOrganisations && (
                    <div className="relative" ref={orgDropdownRef}>
                      <div
                        className="flex justify-between items-center border-[1px] rounded-md p-[0.52rem] text-sm w-full cursor-pointer min-w-[200px] bg-white"
                        onClick={() => {
                          setOrgDropdownOpen(!orgDropdownOpen);
                          if (!orgDropdownOpen) {
                            fetchOrgs();
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            setOrgDropdownOpen(!orgDropdownOpen);
                            if (!orgDropdownOpen) {
                              fetchOrgs();
                            }
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <span className="truncate">
                          {selectedOrganisation
                            ? organisationOptions.find(
                                (opt) => opt.value === selectedOrganisation
                              )?.name
                            : "All Organisations"}
                        </span>
                        <svg
                          className={`w-5 h-5 ml-2 transition-transform duration-200 ${
                            orgDropdownOpen ? "transform rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                      {orgDropdownOpen && (
                        <div
                          className="absolute z-20 mt-1 w-full max-h-60 overflow-auto border border-gray-300 bg-white rounded-md shadow-lg"
                          onScroll={handleOrgScroll}
                        >
                          {organisationOptions.length > 0 ? (
                            organisationOptions.map((option) => (
                              <div
                                key={option.value}
                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer truncate"
                                onClick={() => {
                                  setSelectedOrganisation(option.value);
                                  setOrgCode(option.value);
                                  setOrgDropdownOpen(false);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    setSelectedOrganisation(option.value);
                                    setOrgCode(option.value);
                                    setOrgDropdownOpen(false);
                                  }
                                }}
                                role="button"
                                tabIndex={0}
                              >
                                {option.name}
                              </div>
                            ))
                          ) : (
                            <div className="px-4 py-2 text-gray-500">
                              {orgLoading
                                ? "Loading..."
                                : "No organisations found"}
                            </div>
                          )}
                          {orgLoading && (
                            <div className="px-4 py-2 flex justify-center">
                              Loading...
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
              </div>
            </div>
            {/* Search */}
            <form
              onSubmit={(e) => e.preventDefault()}
              className="bg-cfSecondary p-2 rounded-lg w-full lg-xl:w-[25rem] self-center  md:self-end md:m-0 border mt-2"
            >
              <input
                type="text"
                name="search"
                id="search"
                value={search}
                onChange={(e) => {
                  authContext.IssuedOffsetHandler(0);
                  setSearch(e.target.value.toLowerCase());
                }}
                placeholder="Search for certificate ID, transaction ID"
                className="outline-none border-none p-0 placeholder:text-sm w-full"
                style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
              />
            </form>
          </div>
        </div>
        <div className="overflow-x-auto">
          <div className="flex flex-col justify-center gap-2 min-w-[90.5rem] md:w-full px-8">
            <div className="flex bg-[#E0EAFF] items-center h-[3.563rem] border text-center min-w-fit px-6">
              <div className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                Issuance name
              </div>
              <div className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                Programme Name
              </div>
              <div className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                Batch Name
              </div>
              <div className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                Progress
              </div>
              <div className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                Status
              </div>
              <div className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                Action
              </div>
            </div>
            {isSearching ? (
              <div className="flex justify-center items-center min-h-[8rem] mt-16">
                <CfLoader />
              </div>
            ) : isSearched && respContentList.length === 0 ? (
              <div className="flex justify-center items-center w-full py-3 bg-cfSecondary text-gray-600 font-semibold text-lg">
                No results found
              </div>
            ) : (
              respContentList.map(
                ({
                  transaction_id,
                  issuer_credential_template_id,
                  credential_template_id,
                  certificate_name,
                  transaction_status,
                  preview_image,
                  completed_at,
                  updated_at,
                  issued_by,
                  receivers,
                  userProfileImg,
                  issuance_type,
                  programme_name,
                  batch_name,
                  issuance_status,
                }) => (
                  <IssuedCredentialLongCard
                    key={transaction_id}
                    transaction_id={transaction_id}
                    name={certificate_name}
                    credential_template_id={credential_template_id}
                    issuer_credential_template_id={
                      issuer_credential_template_id
                    }
                    transaction_status={transaction_status}
                    completed_at={completed_at}
                    lastEdited={updated_at}
                    thumbnail_image={preview_image}
                    issued_by={issued_by}
                    userProfileImg={userProfileImg}
                    receivers={receivers}
                    issuance_type={issuance_type}
                    programme_name={programme_name}
                    batch_name={batch_name}
                    issuance_status={issuance_status}
                  />
                )
              )
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-6 px-8 w-full">
        <button
          onClick={() =>
            authContext.IssuedOffsetHandler(authContext.issuedOffset - limit)
          }
          className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
          disabled={previous === null}
        >
          <span className="flex items-center justify-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-5 h-5 rtl:-scale-x-100"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
              />
            </svg>

            <span>previous</span>
          </span>
        </button>

        <button
          onClick={() =>
            authContext.IssuedOffsetHandler(authContext.issuedOffset + limit)
          }
          className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
          disabled={next === null}
        >
          <span className="flex items-center justify-center gap-2">
            <span>Next</span>

            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-5 h-5 rtl:-scale-x-100"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
              />
            </svg>
          </span>
        </button>
      </div>
    </div>
  );
};

export default AllTransactions;
